/* src/pages/Inbox.jsx
 * gocta Operator Console — Inbox (3-pane): list · thread · context rail.
 * Merges Leads.jsx (workspace-wide inbox + per-form browse) and
 * LeadManagement.jsx (per-form actions) into one screen, mounted at both
 * /leads and /leads/:formId. Chat contacts bridged from the widget appear here
 * with source=chat and channel=chat on merged history.
 * and optimistic-update/rollback below is carried over unchanged.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { canAccessLeads, getUserWorkspaceAccess } from '../utils/accessControl';
import { isAdmin } from '../utils/admin';
import { useIsMobile } from '../hooks/useIsMobile';
import { useToast } from '../contexts/ToastContext';
import { leadApi, getRecentEmailRecipients, saveRecentEmailRecipient, smsApi, teamApi } from '../utils/api';
import { INBOX_SMS_COMPOSER } from '../utils/flags';
import LeadListPane from '../components/inbox/LeadListPane';
import ThreadPane from '../components/inbox/ThreadPane';
import ContextRail from '../components/inbox/ContextRail';

function matchesFilters(lead, { searchQuery, qualificationFilter, statusFilter, assignmentFilter, currentUserEmail }) {
  if (statusFilter === 'all' && lead.status === 'blocked') return false;
  if (statusFilter === 'blocked' && lead.status !== 'blocked') return false;
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    const hay = `${lead.leadName || ''} ${lead.leadEmail || ''} ${lead.formName || ''}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (qualificationFilter === 'clean' && lead.spamStatus !== 'clean') return false;
  if (qualificationFilter === 'suspicious' && lead.spamStatus !== 'suspicious') return false;
  if (qualificationFilter === 'booked' && lead.status !== 'booked' && !lead.manuallyBooked) return false;
  if (qualificationFilter === 'needs_followup' && (lead.followUpCount || 0) === 0) return false;
  if (assignmentFilter === 'mine') {
    const assigned = (lead.assignedTo || '').toLowerCase();
    if (!currentUserEmail || assigned !== currentUserEmail.toLowerCase()) return false;
  }
  if (assignmentFilter === 'unassigned' && lead.assignedTo) return false;
  return true;
}

export default function Inbox() {
  const { formId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { selectedWorkspaceId, switchWorkspace, isOwner } = useWorkspace();
  const { showToast } = useToast();
  const isMobile = useIsMobile();

  const [forms, setForms] = useState([]);
  const [form, setForm] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [ownerPaymentRequired, setOwnerPaymentRequired] = useState(false);

  const [statusFilter, setStatusFilter] = useState('all');
  const [qualificationFilter, setQualificationFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedConversationId, setSelectedConversationId] = useState(searchParams.get('conversation') || null);
  const [dealStageOverrides, setDealStageOverrides] = useState({});
  const [dealValueInput, setDealValueInput] = useState('');
  const [noteText, setNoteText] = useState('');
  const [emailText, setEmailText] = useState('');
  const [emailSendError, setEmailSendError] = useState('');
  const [smsText, setSmsText] = useState('');
  const [smsSendError, setSmsSendError] = useState('');
  const [smsReady, setSmsReady] = useState(false);
  const [smsStatusLoading, setSmsStatusLoading] = useState(true);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [leadFormDataCache, setLeadFormDataCache] = useState({});
  const [mobileShowThread, setMobileShowThread] = useState(false);

  const smsComposerEnabled = INBOX_SMS_COMPOSER && smsReady;

  useEffect(() => {
    if (!selectedWorkspaceId) {
      setSmsReady(false);
      setSmsStatusLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSmsStatusLoading(true);
    (async () => {
      try {
        const result = await smsApi.getSmsStatus(selectedWorkspaceId);
        if (cancelled) return;
        const doc = result?.smsNumber;
        setSmsReady(
          doc?.status === 'active' && doc?.a2p?.status === 'approved'
        );
      } catch {
        if (!cancelled) setSmsReady(false);
      } finally {
        if (!cancelled) setSmsStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedWorkspaceId]);

  /* ── Forms listener (per-form browse dropdown), always workspace-scoped ── */
  useEffect(() => {
    if (!selectedWorkspaceId) return undefined;
    const q = query(collection(db, 'forms'), where('workspaceId', '==', selectedWorkspaceId));
    const unsub = onSnapshot(q, (snap) => {
      setForms(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setForms([]));
    return () => unsub();
  }, [selectedWorkspaceId]);

  async function loadTeamMembers(workspaceId) {
    try {
      const data = await teamApi.getMembers(workspaceId);
      setTeamMembers(data.members || []);
    } catch {
      setTeamMembers([]);
    }
  }

  // Per-form inbox is one-shot HTTP (same as the workspace view). A later
  // polling refresh would re-call loadFormConversations on an interval here.
  async function loadFormConversations(fId) {
    try {
      const result = await leadApi.getFormConversations(fId, statusFilter);
      setConversations(result.conversations || []);
    } catch {
      setConversations([]);
      showToast('Failed to load conversations', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function loadWorkspaceConversations(workspaceId) {
    try {
      const result = await leadApi.getWorkspaceConversations(workspaceId, statusFilter);
      setConversations(result.conversations || []);
    } catch {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }

  /* ── Access check + conversation source, re-derived on route/filter change ── */
  useEffect(() => {
    let cancelled = false;
    setAccessDenied(false);
    setOwnerPaymentRequired(false);

    if (!currentUser || (!formId && !selectedWorkspaceId)) {
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);

    async function run() {
      if (formId) {
        try {
          const formDoc = await getDoc(doc(db, 'forms', formId));
          if (cancelled) return;
          if (!formDoc.exists()) {
            showToast('Form not found', 'error');
            navigate('/leads');
            return;
          }
          const formData = { id: formDoc.id, ...formDoc.data() };
          const hasAccess = await canAccessLeads(currentUser.uid, formData.workspaceId);
          if (cancelled) return;
          if (!hasAccess) {
            showToast('Access denied, no lead permissions', 'error');
            navigate('/leads');
            return;
          }
          if (formData.workspaceId !== currentUser.uid && !isAdmin(currentUser, userProfile)) {
            const data = await leadApi.getSubscriptionStatus(formData.workspaceId);
            if (cancelled) return;
            if (!data.hasActiveAccess) {
              navigate('/billing?trial_expired=1');
              return;
            }
          }
          if (cancelled) return;
          if (formData.workspaceId && formData.workspaceId !== selectedWorkspaceId && switchWorkspace) {
            switchWorkspace(formData.workspaceId);
          }
          setForm(formData);
          loadTeamMembers(formData.workspaceId);
          await loadFormConversations(formId);
        } catch {
          if (!cancelled) {
            showToast('Error loading form', 'error');
            navigate('/leads');
          }
        }
      } else {
        setForm(null);
        if (!selectedWorkspaceId) { setLoading(false); return; }
        const access = await getUserWorkspaceAccess(currentUser.uid, selectedWorkspaceId);
        if (cancelled) return;
        if (!access.leadAccess && !access.isOwner) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        if (selectedWorkspaceId !== currentUser.uid && !isAdmin(currentUser, userProfile)) {
          try {
            const data = await leadApi.getSubscriptionStatus(selectedWorkspaceId);
            if (cancelled) return;
            if (!data.hasActiveAccess) {
              setOwnerPaymentRequired(true);
              setLoading(false);
              return;
            }
          } catch {
            if (!cancelled) {
              setOwnerPaymentRequired(true);
              setLoading(false);
            }
            return;
          }
        }
        if (cancelled) return;
        loadTeamMembers(selectedWorkspaceId);
        loadWorkspaceConversations(selectedWorkspaceId);
      }
    }

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, currentUser, selectedWorkspaceId, statusFilter]);

  /* ── Deep-link selection: ?conversation= ── */
  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId && convId !== selectedConversationId) setSelectedConversationId(convId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const selectedConversation = conversations.find((c) => c.id === selectedConversationId) || null;

  useEffect(() => {
    if (!selectedConversation?.id) return;
    const val = selectedConversation.dealValue ?? '';
    setDealValueInput(val === null || val === undefined ? '' : String(val));
  }, [selectedConversation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedConversation?.id) return;
    const hasStored = (selectedConversation.formData || selectedConversation.initialFormData) &&
      Object.keys(selectedConversation.formData || selectedConversation.initialFormData || {}).length > 0;
    if (hasStored || !selectedConversation.submissionId) return;
    let cancelled = false;
    leadApi.getLeadFormData(selectedConversation.id).then((data) => {
      if (!cancelled && data?.formData && Object.keys(data.formData).length > 0) {
        setLeadFormDataCache((prev) => ({ ...prev, [selectedConversation.id]: data.formData }));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedConversation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function refreshIfWorkspaceWide() {
    if (!formId && selectedWorkspaceId) loadWorkspaceConversations(selectedWorkspaceId);
  }

  async function runAction(fn, successMsg) {
    setActionLoading(true);
    try {
      const result = await fn();
      if (result.success) {
        showToast(successMsg);
        refreshIfWorkspaceWide();
      } else {
        showToast(result.error || 'Action failed', 'error');
      }
      return result;
    } catch {
      showToast('Action failed', 'error');
      return { success: false };
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSendEmail() {
    if (!selectedConversation?.id || !emailText.trim()) return { success: false };
    setActionLoading(true);
    setEmailSendError('');
    try {
      const result = await leadApi.sendManualReply(selectedConversation.id, emailText.trim());
      if (result.success) {
        setEmailText('');
        showToast('Email sent');
        refreshIfWorkspaceWide();
        return result;
      }
      setEmailSendError(result.error || 'Failed to send email');
      return result;
    } catch (e) {
      const msg = e.message || 'Failed to send email';
      setEmailSendError(msg);
      return { success: false, error: msg };
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSendSms() {
    if (!selectedConversation?.id || !smsText.trim()) return { success: false };
    setActionLoading(true);
    setSmsSendError('');
    try {
      const result = await leadApi.sendManualSms(selectedConversation.id, smsText.trim());
      if (result.success) {
        setSmsText('');
        showToast('SMS sent');
        refreshIfWorkspaceWide();
        return result;
      }
      setSmsSendError(result.error || 'Failed to send SMS');
      return result;
    } catch (e) {
      const msg = e.message || 'Failed to send SMS';
      setSmsSendError(msg);
      return { success: false, error: msg };
    } finally {
      setActionLoading(false);
    }
  }

  async function handleStageChange(stage, dealValue) {
    const convId = selectedConversation.id;
    const prevValue = selectedConversation.dealValue ?? null;
    setDealStageOverrides((prev) => ({ ...prev, [convId]: { stage, dealValue } }));
    try {
      const result = await leadApi.updateDealStage(convId, stage, dealValue);
      if (result.success) {
        showToast('Deal stage updated');
        refreshIfWorkspaceWide();
      } else {
        showToast(result.error || 'Failed to update deal stage', 'error');
        setDealValueInput(prevValue === null || prevValue === undefined ? '' : String(prevValue));
      }
    } catch {
      showToast('Failed to update deal stage', 'error');
      setDealValueInput(prevValue === null || prevValue === undefined ? '' : String(prevValue));
    } finally {
      setDealStageOverrides((prev) => { const next = { ...prev }; delete next[convId]; return next; });
    }
  }

  function handleSelectLead(lead) {
    setSelectedConversationId(lead.id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('conversation', lead.id);
      return next;
    });
    if (isMobile) setMobileShowThread(true);
  }

  function handleFormChange(newFormId) {
    setSelectedConversationId(null);
    navigate(newFormId ? `/leads/${newFormId}` : '/leads');
  }

  const recentEmails = getRecentEmailRecipients();
  const filteredLeads = conversations.filter((lead) => matchesFilters(lead, {
    searchQuery,
    qualificationFilter,
    statusFilter,
    assignmentFilter,
    currentUserEmail: currentUser?.email,
  }));
  const effectiveStage = selectedConversation
    ? (dealStageOverrides[selectedConversation.id]?.stage ?? selectedConversation.dealStage ?? 'new')
    : 'new';
  const effectiveDealValue = selectedConversation
    ? (dealStageOverrides[selectedConversation.id]?.dealValue ?? selectedConversation.dealValue ?? null)
    : null;

  if (ownerPaymentRequired) {
    return (
      <div className="console-shell" style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline-5)', borderRadius: '12px', padding: '2rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0, color: 'var(--danger)' }}>Payment required</h2>
          <p style={{ color: 'var(--muted)' }}>The account owner needs to add a payment method to restore access to leads.</p>
          <button
            onClick={() => navigate('/billing')}
            className="inbox-pill-btn-primary"
            style={{ marginTop: '1rem' }}
          >
            Go to billing
          </button>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="console-shell" style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline-5)', borderRadius: '12px', padding: '2rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0, color: 'var(--ink)' }}>Access denied</h2>
          <p style={{ color: 'var(--muted)' }}>You do not have lead access for this workspace. Ask the owner to grant lead permissions.</p>
        </div>
      </div>
    );
  }

  const listPane = (
    <LeadListPane
      leads={filteredLeads}
      loading={loading}
      selectedId={selectedConversationId}
      onSelect={handleSelectLead}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      qualificationFilter={qualificationFilter}
      onQualificationFilterChange={setQualificationFilter}
      assignmentFilter={assignmentFilter}
      onAssignmentFilterChange={setAssignmentFilter}
      forms={forms}
      formId={formId || null}
      onFormChange={handleFormChange}
      mobile={isMobile}
    />
  );

  const threadAndRail = selectedConversation ? (
    <>
      <ThreadPane
        conversation={selectedConversation}
        form={form}
        effectiveStage={effectiveStage}
        effectiveDealValue={effectiveDealValue}
        dealValueInput={dealValueInput}
        onDealValueInputChange={setDealValueInput}
        onDealValueCommit={() => {
          const parsed = dealValueInput === '' ? null : parseFloat(dealValueInput);
          handleStageChange(effectiveStage, Number.isNaN(parsed) ? null : parsed);
        }}
        onStageChange={handleStageChange}
        onToggleOperator={() => runAction(
          () => (selectedConversation.aiEnabled === false ? leadApi.turnOnAI(selectedConversation.id) : leadApi.turnOffAI(selectedConversation.id)),
          selectedConversation.aiEnabled === false ? 'AI turned on' : 'AI turned off'
        )}
        actionLoading={actionLoading}
        onMarkContacted={() => runAction(() => leadApi.markContacted(selectedConversation.id), 'Marked as contacted')}
        onMarkBooked={() => runAction(() => leadApi.markBooked(selectedConversation.id), 'Marked as booked')}
        teamMembers={teamMembers}
        onDelegate={(assignedTo) => runAction(() => leadApi.delegate(selectedConversation.id, assignedTo, 'manual'), 'Lead assigned')}
        emailRecipient={emailRecipient}
        onEmailRecipientChange={setEmailRecipient}
        onEmailToTeam={async () => {
          const r = await runAction(() => leadApi.emailToTeam(selectedConversation.id, emailRecipient), 'Lead emailed to team');
          if (r.success) saveRecentEmailRecipient(emailRecipient);
          return r;
        }}
        recentEmails={recentEmails}
        noteText={noteText}
        onNoteTextChange={setNoteText}
        onAddNote={async () => {
          const r = await runAction(() => leadApi.addNote(selectedConversation.id, noteText), 'Note added');
          if (r.success) setNoteText('');
        }}
        emailText={emailText}
        onEmailTextChange={setEmailText}
        onSendEmail={handleSendEmail}
        emailSendError={emailSendError}
        onClearEmailError={() => setEmailSendError('')}
        smsText={smsText}
        onSmsTextChange={setSmsText}
        onSendSms={handleSendSms}
        smsSendError={smsSendError}
        onClearSmsError={() => setSmsSendError('')}
        smsComposerEnabled={smsComposerEnabled}
        smsReady={smsReady}
        smsStatusLoading={smsStatusLoading}
        mobile={isMobile}
        onBack={isMobile ? () => setMobileShowThread(false) : undefined}
        onMarkSpamAndBlock={async ({ blockDomain }) => {
          const convId = selectedConversation.id;
          const result = await runAction(
            () => leadApi.markSpamAndBlock(convId, { blockDomain }),
            blockDomain ? 'Sender and domain blocked' : 'Sender blocked'
          );
          if (result.success) {
            showToast(
              blockDomain ? 'Marked as spam — address and domain blocked' : 'Marked as spam — sender blocked',
              'success',
              {
                actionLabel: 'Undo',
                onAction: async () => {
                  const undo = await leadApi.unblockConversation(convId);
                  if (undo.success) {
                    showToast('Block undone');
                    refreshIfWorkspaceWide();
                  } else {
                    showToast(undo.error || 'Undo failed', 'error');
                  }
                },
              }
            );
          }
          return result;
        }}
        onUnblock={() => runAction(
          () => leadApi.unblockConversation(selectedConversation.id),
          'Lead unblocked'
        )}
        onApproveSuspicious={() => runAction(
          () => leadApi.approveSuspiciousLead(selectedConversation.id),
          'AI reply queued — lead marked clean'
        )}
        isOwner={isOwner}
        onRetryConsentLedger={() => runAction(
          () => leadApi.retryConsentLedger(selectedWorkspaceId, selectedConversation.id),
          'Consent record saved'
        )}
      />
      <ContextRail
        conversation={selectedConversation}
        leadFormData={leadFormDataCache[selectedConversation.id]}
        currentUserEmail={currentUser?.email}
        actionLoading={actionLoading}
        onClaim={() => currentUser?.email
          ? runAction(
            () => leadApi.delegate(selectedConversation.id, currentUser.email, 'claim'),
            'Lead claimed'
          )
          : Promise.resolve({ success: false })}
        onUnassign={() => runAction(
          () => leadApi.delegate(selectedConversation.id, '', 'unassign'),
          'Lead unassigned'
        )}
        mobile={isMobile}
      />
    </>
  ) : (
    <ThreadPane conversation={null} mobile={isMobile} />
  );

  if (isMobile) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh' }}>
        {mobileShowThread && selectedConversation ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>{threadAndRail}</div>
        ) : (
          listPane
        )}
      </div>
    );
  }

  return (
    <div className="console-shell" style={{ display: 'flex', minHeight: '100vh' }}>
      {listPane}
      <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
        {threadAndRail}
      </div>
    </div>
  );
}
